import { Button, Grid } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import {
  Effect,
  EffectType,
  Instrument,
  InstrumentType,
  MidiNote,
  StepType,
  Track,
} from "../../../reactronica";
import { SongContext } from "../../../reactronica/components/Song";
import PianoRoll from "../../PianoRoll/PianoRoll";
import { ButtonAttributes, notes } from "../../PianoRoll/types";
import TrackControls from "../TrackControls/TrackControls";
import { Item, styles } from "./styles";
import { Props } from "./types";
import useInstrumentTrack from "./useInstrumentTrack";

export const COLUMNS = 64;

const InstrumentTrack: React.FC<Props> = ({
  selected,
  trackProperties,
  handleSelectTrack,
  handleRemoveTrack,
  handleTrackFinished,
  onNameFieldFocusChange
}) => {
  const cells: ButtonAttributes[][] = Array.from(notes, (note) =>
    Array.from(Array(COLUMNS).keys(), (column) => ({ column, note }))
  );

  const {
    track,
    updateTrackSteps,
    handleNameChange,
    handleInstrumentTypeChange,
    handlePanChange,
    handleMuteChange,
    handleSoloChange,
    handleVolumeChange,
    handleEffectChange,
  } = useInstrumentTrack(trackProperties);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const { isPlaying, exporting } = useContext(SongContext);

  useEffect(() => {
    if (!isPlaying) {
      setCurrentStepIndex(0);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying || exporting) {
      if (track.steps.length === 0) {
        handleTrackFinished(track.id, true);
      }
    } else {
      handleTrackFinished(track.id, undefined);
    }
  }, [exporting, handleTrackFinished, isPlaying, track.id, track.steps.length]);

  return (
    <>
      {selected && (
        <PianoRoll
          currentStepIndex={currentStepIndex}
          steps={track.steps ?? []}
          instrument={track.instrument?.type}
          onClick={(steps: StepType[]) => updateTrackSteps(steps)}
        />
      )}
      <TrackControls
        trackProperties={track}
        handleNameChange={handleNameChange}
        handleInstrumentTypeChange={handleInstrumentTypeChange}
        handlePanChange={handlePanChange}
        handleMuteChange={handleMuteChange}
        handleSoloChange={handleSoloChange}
        handleVolumeChange={handleVolumeChange}
        handleEffectChange={handleEffectChange}
        handleRemoveTrack={handleRemoveTrack}
        onNameFieldFocusChange={onNameFieldFocusChange}
      />
      <Button
        variant="outlined"
        sx={styles.stepsSnapshot}
        onClick={() => handleSelectTrack(track.id)}
      >
        <Grid container spacing={0} columns={COLUMNS}>
          {cells
            .map((rows) =>
              rows.map(({ column, note }) => (
                <Grid key={`${note}${column}`} item xs={1}>
                  <Item
                    notesSize={notes.length}
                    isClicked={(
                      track.steps![column] as MidiNote[] | null
                    )?.includes(note as MidiNote)}
                    trackSelected={selected}
                  />
                </Grid>
              ))
            )
            .flat()}
        </Grid>
      </Button>
      <Track
        id={track.id}
        volume={track.volume}
        mute={track.mute}
        solo={track.solo}
        pan={track.pan}
        steps={track.steps}
        onStepPlay={(_stepNotes, index) => {
          if (currentStepIndex !== index) {
            setCurrentStepIndex(index);
          }
        }}
        handleTrackFinished={handleTrackFinished}
      >
        <>
          {track.instrument!.type === "sampler" && (
            <Instrument
              type={track.instrument!.type as InstrumentType}
              samples={{
                B5: "/Sounds/snare1.mp3",
                G5: "/Sounds/kick2.mp3",
                A5: "/Sounds/kick1.mp3",
                F5: "/Sounds/clap1.wav",
                E5: "/Sounds/hi1.wav",
                D5: "/Sounds/perc1.wav",
              }}
            />
          )}
          {track.instrument!.type !== "sampler" && (
            <Instrument type={track.instrument!.type as InstrumentType} />
          )}
          {track.effects?.map((effect) => (
            <Effect key={effect.type} type={effect.type as EffectType} />
          ))}
        </>
      </Track>
    </>
  );
};

export default InstrumentTrack;
